import { Notification } from 'modules/core/domain/interfaces/supabase/notifications/types';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'store/store';
import { fetchNotifications } from 'modules/notifications/store/actions';
import {
  setGlobalLastReadId as setGlobalLastReadIdAction,
  setPageLastReadId as setPageLastReadIdAction,
} from 'modules/notifications/store/notifications';
import {
  selectGlobalLastReadId,
  selectNotifications,
  selectNotificationsLoading,
  selectPageLastReadId,
} from 'modules/notifications/store/selectors';

/**
 * Хук для работы с непрочитанными уведомлениями.
 *
 * Правила:
 * - Уведомление считается прочитанным сразу после загрузки его из Supabase (глобально для системы).
 * - На странице уведомлений визуально они остаются «непрочитанными», пока пользователь не нажмёт
 *   «Прочитать все» (это страничная отметка).
 */
type Options = { autoFetch?: boolean; markOnLoad?: boolean };
export const useUnreadNotifications = (options: Options = {}) => {
  const notifications = useSelector(selectNotifications);
  const loading = useSelector(selectNotificationsLoading);
  const dispatch = useDispatch<AppDispatch>();
  const { info, isLoaded } = useOrganizer();
  const organizerId = info?.id;
  const requestedRef = useRef(false);

  const globalLastReadId = useSelector(selectGlobalLastReadId);
  const pageLastReadId = useSelector(selectPageLastReadId);

  // Автозагрузка уведомлений, если хук используется глобально (например, в меню)
  useEffect(() => {
    const auto = options.autoFetch !== false;
    if (!auto) return;
    if (requestedRef.current) return;
    if (loading) return;
    if (!isLoaded) return;
    if (notifications.length > 0) return;
    requestedRef.current = true;
    dispatch(
      fetchNotifications({
        userId: organizerId,
        limit: 10,
        offset: 0,
        sort: 'desc',
      }),
    );
  }, [options.autoFetch, loading, isLoaded, notifications.length, dispatch, organizerId]);

  // После загрузки уведомлений из Supabase — считаем их прочитанными глобально
  useEffect(() => {
    if (!notifications.length) return;
    if (!options.markOnLoad) return; // В меню не помечаем автоматически
    const maxId = notifications.reduce((max, n) => (n.id > max ? n.id : max), notifications[0].id);
    if (globalLastReadId == null || maxId > globalLastReadId) {
      dispatch(setGlobalLastReadIdAction(maxId));
    }
  }, [notifications, globalLastReadId, options.markOnLoad, dispatch]);

  const unreadNotifications = useMemo<Notification[]>(() => {
    const threshold = globalLastReadId ?? 0;
    return notifications.filter((n) => n.id > threshold);
  }, [notifications, globalLastReadId]);

  const unreadCount = unreadNotifications.length;

  const markAllAsRead = useCallback(() => {
    if (!notifications.length) return;
    const maxId = notifications.reduce((max, n) => (n.id > max ? n.id : max), notifications[0].id);
    // Обновляем глобально и для визуального состояния на странице через store
    dispatch(setGlobalLastReadIdAction(maxId));
    dispatch(setPageLastReadIdAction(maxId));
  }, [notifications, dispatch]);

  return {
    unreadCount,
    unreadNotifications,
    globalLastReadId,
    pageLastReadId,
    markAllAsRead,
  };
};

export default useUnreadNotifications;
